import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress,
    MenuItem, Checkbox, FormControlLabel, IconButton, Chip, Divider
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { crearTramiteJubilacion, getChecklistFormularios, getTramitesJubilacion } from '../../services/jubilacionService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import PageHeader from '../ui/PageHeader';
import useRegimenesJubilacion from './useRegimenesJubilacion';
import ModalNuevoRegimenJubilacion from './ModalNuevoRegimenJubilacion';

const SUBTIPOS = [
    { value: 'ORDINARIA', label: 'Ordinaria' },
    { value: 'REGIMEN_DIFERENCIAL', label: 'Régimen Diferencial' },
    { value: 'CASAS_PARTICULARES', label: 'Casas Particulares' },
];

// Opción del desplegable de régimen que abre el alta de un régimen nuevo (no es un valor real).
const OPCION_NUEVO_REGIMEN = '__nuevo__';

const EMPLEADOR_VACIO = { nombre: '', tieneContacto: false, fallecido: false, tieneRecibos: false };

// Deriva la edad a partir de la fecha de nacimiento (YYYY-MM-DD); sin fecha, sin edad.
const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return '';
    const hoy = new Date();
    const nac = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nac.getFullYear();
    const m = hoy.getMonth() - nac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
    return edad >= 0 ? edad : '';
};

/**
 * Formulario para iniciar un trámite del área Jubilación. Una sola pantalla con render
 * condicional por subtipo + preview en vivo del checklist de formularios.
 */
// fechaLiquidarInicial: precarga la fecha de liquidar (la apertura del plan de pago la propone en la
// fecha en que el cliente cumple la edad jubilatoria). Se puede cambiar antes de guardar.
export default function FormularioIniciarTramiteJubilacion({ onSuccess, onCancel, cuilInicial, idTurnoOrigen, fechaLiquidarInicial }) {
    const navigate = useNavigate();
    const { registrarPacto, adjuntarATramite } = usePactosPendientes();
    const location = useLocation();
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial || cuilParam;
    const clienteInicial = location.state?.clienteInicial || location.state?.cliente;

    const [form, setForm] = useState({
        idCliente: '',
        fechaInicioEstimada: fechaLiquidarInicial ? String(fechaLiquidarInicial).slice(0, 10) : '',
        idUsuario: user?.id || null,
        subtipo: '',
        idRegimenDiferencial: '',
        organismo: '',
        aportesProvinciaNoTransferida: false,
        generaSicam: false,
        comentarios: '',
    });
    const [empleadores, setEmpleadores] = useState([]);

    // --- Régimen diferencial (dato maestro, con alta al vuelo) ---
    const { regimenes, agregarRegimen } = useRegimenesJubilacion();
    const [modalRegimenOpen, setModalRegimenOpen] = useState(false);
    const altaRegimenPendienteRef = useRef(false);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [guardando, setGuardando] = useState(false);

    // --- Búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const busquedaTimeoutRef = useRef(null);

    // --- Trámite de Jubilación duplicado para el mismo cliente (patrón RTI/ART) ---
    const [tieneDuplicadoJubilacion, setTieneDuplicadoJubilacion] = useState(false);
    const [cantidadDuplicados, setCantidadDuplicados] = useState(0);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // --- Preview del checklist ---
    const [checklist, setChecklist] = useState([]);
    const [cargandoChecklist, setCargandoChecklist] = useState(false);
    const checklistTimeoutRef = useRef(null);

    const esCasasParticulares = form.subtipo === 'CASAS_PARTICULARES';
    const esRegimenDiferencial = form.subtipo === 'REGIMEN_DIFERENCIAL';
    const regimenSeleccionado = regimenes.find(r => r.id === form.idRegimenDiferencial);
    // Mismo criterio que el submit (empleadores.filter con nombre no vacío): un empleador sin
    // nombre todavía no se manda al crear el trámite, así que tampoco debe contar acá — si no,
    // el preview podía mostrar un documento que después no aparecía en el trámite ya creado.
    const empleadoresValidos = empleadores.filter(emp => emp.nombre && emp.nombre.trim() !== '');
    const hijosDelCliente = clienteSeleccionado?.hijos || [];

    // Documentos que le corresponden a UN empleador puntual, según su propia situación (no la
    // del resto): PS 2.93 si está vivo y hay contacto, PS 2.94 si falleció o no hay contacto,
    // Recibos si declaró tenerlos. Solo informativo acá — se adjuntan recién en la ficha.
    const documentosRequeridosEmpleador = (emp) => {
        const docs = [];
        if (emp.tieneContacto && !emp.fallecido) docs.push('PS 2.93');
        if (emp.fallecido || !emp.tieneContacto) docs.push('PS 2.94');
        if (emp.tieneRecibos) docs.push('Recibos');
        return docs;
    };

    // Carga inicial de cliente si viene por URL
    useEffect(() => {
        const cargar = async () => {
            try {
                if (clienteInicial?.cuil || clienteInicial?.cuilCliente || clienteInicial?.dni) {
                    const cliente = clienteInicial;
                    setClienteSeleccionado(cliente);
                    setForm(prev => ({ ...prev, idCliente: cliente.cuil || cliente.cuilCliente || cliente.dni || '' }));
                    return;
                }
                if (!cuilEfectivo) return;
                const cliente = await getClientePorCuil(cuilEfectivo);
                setClienteSeleccionado(cliente);
                setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
            } catch (err) {
                console.error('Error al cargar cliente inicial:', err);
            }
        };
        cargar();
    }, [cuilEfectivo, clienteInicial]);

    // Trámite de Jubilación ya existente para el cliente seleccionado (patrón RTI/ART).
    // Jubilación no tiene estados de cierre, así que "activo" = existe cualquier trámite con ese CUIL.
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicadoJubilacion(false);
            setCantidadDuplicados(0);
            setConfirmarDuplicado(false);
            return;
        }

        let activo = true;
        const verificarDuplicado = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramitesJubilacion(cuilCliente);
                const items = Array.isArray(tramites) ? tramites : [];
                if (activo) {
                    setCantidadDuplicados(items.length);
                    setTieneDuplicadoJubilacion(items.length > 0);
                    if (items.length === 0) {
                        setConfirmarDuplicado(false);
                    }
                }
            } catch (err) {
                console.error('Error validando trámites de Jubilación duplicados:', err);
                if (activo) {
                    setTieneDuplicadoJubilacion(false);
                    setCantidadDuplicados(0);
                }
            } finally {
                if (activo) setCargandoDuplicados(false);
            }
        };

        verificarDuplicado();
        return () => { activo = false; };
    }, [form.idCliente]);

    // Preview en vivo del checklist (depende solo de campos del formulario → resolver puro)
    useEffect(() => {
        if (!form.subtipo) {
            setChecklist([]);
            return;
        }
        if (checklistTimeoutRef.current) clearTimeout(checklistTimeoutRef.current);
        setCargandoChecklist(true);
        checklistTimeoutRef.current = setTimeout(async () => {
            try {
                const data = await getChecklistFormularios({
                    subtipo: form.subtipo,
                    idRegimen: esRegimenDiferencial ? (form.idRegimenDiferencial || null) : null,
                    aportesProvinciaNoTransferida: form.aportesProvinciaNoTransferida,
                    generaSicam: form.generaSicam,
                });
                setChecklist(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error al previsualizar checklist:', err);
                setChecklist([]);
            } finally {
                setCargandoChecklist(false);
            }
        }, 350);
        return () => clearTimeout(checklistTimeoutRef.current);
    }, [form.subtipo, form.idRegimenDiferencial, form.aportesProvinciaNoTransferida, form.generaSicam, esRegimenDiferencial]);

    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (err) {
                console.error('Error al buscar clientes', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCambioRegimen = (e) => {
        const { value } = e.target;
        if (value === OPCION_NUEVO_REGIMEN) {
            // El valor actual no cambia hasta que se cree el régimen. El diálogo se abre recién cuando
            // el menú del select terminó de cerrarse (ver abrirAltaRegimenSiCorresponde): así el menú
            // ya soltó el bloqueo de scroll y devolvió el foco al select, y no le roba el foco al
            // campo del diálogo.
            altaRegimenPendienteRef.current = true;
            return;
        }
        setForm(prev => ({ ...prev, idRegimenDiferencial: value }));
    };

    const abrirAltaRegimenSiCorresponde = () => {
        if (altaRegimenPendienteRef.current) {
            altaRegimenPendienteRef.current = false;
            setModalRegimenOpen(true);
        }
    };

    const handleRegimenCreado = (regimen) => {
        agregarRegimen(regimen);
        setForm(prev => ({ ...prev, idRegimenDiferencial: regimen.id }));
        setModalRegimenOpen(false);
    };

    const handleCheck = (e) => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked }));
    };

    // --- Empleadores domésticos ---
    const agregarEmpleador = () => setEmpleadores(prev => [...prev, { ...EMPLEADOR_VACIO }]);
    const quitarEmpleador = (idx) => setEmpleadores(prev => prev.filter((_, i) => i !== idx));
    const cambiarEmpleador = (idx, campo, valor) =>
        setEmpleadores(prev => prev.map((e, i) => {
            if (i !== idx) return e;
            // "Mantiene contacto" y "Fallecido" son excluyentes: tildar uno destilda el otro.
            // "Tiene recibos" queda libre, no participa de esta regla.
            if (campo === 'tieneContacto' && valor) return { ...e, tieneContacto: true, fallecido: false };
            if (campo === 'fallecido' && valor) return { ...e, fallecido: true, tieneContacto: false };
            return { ...e, [campo]: valor };
        }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) { setError('Debe seleccionar un cliente.'); return; }
        if (!form.subtipo) { setError('Debe seleccionar el subtipo de jubilación.'); return; }
        if (esRegimenDiferencial && !form.idRegimenDiferencial) {
            setError('Debe seleccionar el régimen diferencial.');
            return;
        }
        if (tieneDuplicadoJubilacion && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear otro trámite de Jubilación para un cliente que ya tiene uno.');
            return;
        }
        setError('');
        setSuccess('');
        setGuardando(true);
        try {
            const payload = {
                idCliente: form.idCliente,
                idUsuario: form.idUsuario,
                subtipo: form.subtipo,
                idRegimenDiferencial: esRegimenDiferencial ? (form.idRegimenDiferencial || null) : null,
                organismo: form.organismo || null,
                aportesProvinciaNoTransferida: form.aportesProvinciaNoTransferida,
                generaSicam: form.generaSicam,
                comentarios: form.comentarios,
                empleadores: esCasasParticulares ? empleadoresValidos : [],
                fechaInicioEstimada: form.fechaInicioEstimada || null,
                idTurnoOrigen: idTurnoOrigen || null,
            };
            const creado = await crearTramiteJubilacion(payload);
            // El pacto se emitió antes de que el trámite existiera: recién ahora se puede adjuntar.
            await adjuntarATramite(creado.idTramite);
            setSuccess(`Trámite de Jubilación creado con éxito. ID: ${creado.idTramite}`);
            if (onSuccess) {
                onSuccess(creado);
            } else {
                navigate(`/jubilacion/tramites/${creado.idTramite}`);
            }
        } catch (err) {
            setError(err.response?.data || err.response?.data?.message || 'Error al crear el trámite.');
        } finally {
            setGuardando(false);
        }
    };

    const seccionTitulo = (icono, texto) => (
        <div style={{
            background: 'var(--primary-bg)',
            padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', marginTop: '0.5rem'
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)', fontSize: 'var(--fs-texto-lg)' }}>
                <i className={`bi ${icono} me-2`}></i>{texto}
            </Typography>
        </div>
    );

    return (
        <div className="area-rti">
            <Box sx={{ background: 'var(--bg-page)', minHeight: '100vh', padding: 3 }}>
                <Box sx={{ maxWidth: 900, margin: 'auto', background: 'white', borderRadius: '16px', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                    <PageHeader
                        variante="banner"
                        icono="person-vcard"
                        titulo="Iniciar Trámite de Jubilación"
                        descripcion="Complete los datos para registrar un nuevo trámite previsional"
                    />

                    <Box sx={{ padding: '2rem' }}>
                    <ModalRegistrarCliente open={modalClienteOpen} onClose={() => setModalClienteOpen(false)} onSuccess={handleRegistroExitoso} mostrarDialogoConfirmacion={false} />
                    <ModalNuevoRegimenJubilacion show={modalRegimenOpen} onHide={() => setModalRegimenOpen(false)} onCreado={handleRegimenCreado} />

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            {/* Cliente */}
                            {seccionTitulo('bi-person-check', 'Seleccionar Cliente')}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    id="buscar-cliente-jubilacion"
                                    options={opcionesCliente}
                                    getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                    onInputChange={handleBusquedaCliente}
                                    onChange={handleSeleccionCliente}
                                    value={clienteSeleccionado}
                                    isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                    noOptionsText="No se encontraron clientes"
                                    loading={buscando}
                                    loadingText="Buscando..."
                                    sx={{ flexGrow: 1 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar cliente por CUIL, Apellido o Nombre"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button variant="outlined" onClick={() => setModalClienteOpen(true)} sx={{ height: '56px', flexShrink: 0 }}>
                                    Registrar Cliente
                                </Button>
                            </Box>

                            <SeccionGenerarPacto cliente={clienteSeleccionado} onPactoGenerado={registrarPacto} />

                            {/* Datos del trámite */}
                            {seccionTitulo('bi-file-earmark-text', 'Datos del Trámite')}

                            <TextField select label="Subtipo de Jubilación" name="subtipo" value={form.subtipo} onChange={handleChange} fullWidth required>
                                <MenuItem value=""><em>Seleccione…</em></MenuItem>
                                {SUBTIPOS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                            </TextField>

                            {esRegimenDiferencial && (
                                <TextField select label="Régimen Diferencial" name="idRegimenDiferencial" value={form.idRegimenDiferencial} onChange={handleCambioRegimen} fullWidth required
                                    slotProps={{ select: { MenuProps: { slotProps: { transition: { onExited: abrirAltaRegimenSiCorresponde } } } } }}>
                                    <MenuItem value=""><em>Seleccione…</em></MenuItem>
                                    {regimenes.map(r => <MenuItem key={r.id} value={r.id}>{r.nombre}</MenuItem>)}
                                    <MenuItem value={OPCION_NUEVO_REGIMEN}
                                        sx={{ color: 'var(--primary)', fontWeight: 600, borderTop: '1px solid #e5e7eb' }}>
                                        <AddIcon fontSize="small" sx={{ mr: 1 }} />Crear nuevo régimen…
                                    </MenuItem>
                                </TextField>
                            )}
                            {esRegimenDiferencial && regimenSeleccionado && !regimenSeleccionado.esSistema && (
                                <Alert severity="info" sx={{ borderRadius: '10px' }}>
                                    Régimen agregado por el estudio: lleva la documentación genérica de régimen diferencial
                                    más la que tenga cargada en Datos Maestros → Regímenes de Jubilación.
                                </Alert>
                            )}

                            <TextField select label="Organismo" name="organismo" value={form.organismo} onChange={handleChange} fullWidth
                                helperText="Seleccione si el trámite se gestiona ante Provincia o ANSES.">
                                <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                <MenuItem value="ANSES">ANSES</MenuItem>
                                <MenuItem value="PROVINCIA">Provincia</MenuItem>
                            </TextField>

                            {/* Hijos del cliente (autocompletados desde el Cliente, read-only) */}
                            {clienteSeleccionado?.genero === 'FEMENINO' && (
                                <>
                                    {seccionTitulo('bi-people', 'Hijos del cliente')}
                                    {hijosDelCliente.length === 0 ? (
                                        <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                            El cliente es FEMENINO y no tiene hijos cargados. Cargar los hijos en la ficha del cliente.
                                        </Alert>
                                    ) : (
                                        <Stack spacing={1}>
                                            {hijosDelCliente.map((h, idx) => (
                                                <Box key={idx} sx={{ border: '1px solid #e5e7eb', borderRadius: '10px', p: 2, background: '#fafafa' }}>
                                                    <Typography variant="body1" fontWeight={600}>{h.nombre || 'Sin nombre'}</Typography>
                                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                                        <Chip size="small" label={`Edad: ${calcularEdad(h.fechaNacimiento) || '—'}`} />
                                                        {h.tieneAuh && <Chip size="small" color="primary" variant="outlined" label="AUH" />}
                                                        {h.tieneCud && <Chip size="small" color="primary" variant="outlined" label="CUD" />}
                                                        {h.esAdoptado && <Chip size="small" color="primary" variant="outlined" label="Adoptado/a" />}
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </>
                            )}

                            <FormControlLabel
                                control={<Checkbox name="aportesProvinciaNoTransferida" checked={form.aportesProvinciaNoTransferida} onChange={handleCheck} />}
                                label="Aporta en una provincia que no transfirió su caja a Nación (reconocimiento de servicios)"
                            />
                            <FormControlLabel
                                control={<Checkbox name="generaSicam" checked={form.generaSicam} onChange={handleCheck} />}
                                label="Tiene aportes como MTB / autónomo (genera SICAM)"
                            />

                            {/* Empleadores domésticos */}
                            {esCasasParticulares && (
                                <>
                                    {seccionTitulo('bi-people', 'Empleadores domésticos')}
                                    {empleadores.length === 0 && (
                                        <Typography variant="body2" color="text.secondary">Agregue los empleadores para determinar los formularios PS 2.92 / 2.93 / 2.94.</Typography>
                                    )}
                                    {empleadores.map((emp, idx) => (
                                        <Box key={idx} sx={{ border: '1px solid #e5e7eb', borderRadius: '10px', p: 2 }}>
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                <TextField label={`Empleador #${idx + 1}`} value={emp.nombre}
                                                    onChange={(e) => cambiarEmpleador(idx, 'nombre', e.target.value)} fullWidth />
                                                <IconButton aria-label="Quitar" color="error" onClick={() => quitarEmpleador(idx)}>
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Box>
                                            <Stack direction="row" flexWrap="wrap" sx={{ mt: 1 }}>
                                                <FormControlLabel control={<Checkbox checked={emp.tieneContacto} onChange={(e) => cambiarEmpleador(idx, 'tieneContacto', e.target.checked)} />} label="Mantiene contacto" />
                                                <FormControlLabel control={<Checkbox checked={emp.fallecido} onChange={(e) => cambiarEmpleador(idx, 'fallecido', e.target.checked)} />} label="Fallecido" />
                                                <FormControlLabel control={<Checkbox checked={emp.tieneRecibos} onChange={(e) => cambiarEmpleador(idx, 'tieneRecibos', e.target.checked)} />} label="Tiene recibos" />
                                            </Stack>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                                {documentosRequeridosEmpleador(emp).map(doc => (
                                                    <Chip key={doc} size="small" color="primary" variant="outlined" label={`Requiere: ${doc}`} />
                                                ))}
                                            </Stack>
                                        </Box>
                                    ))}
                                    <Button startIcon={<AddIcon />} variant="outlined" onClick={agregarEmpleador} sx={{ alignSelf: 'flex-start' }}>
                                        Agregar empleador
                                    </Button>
                                </>
                            )}

                            <CampoFechaLiquidar
                                valor={form.fechaInicioEstimada}
                                onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                            />

                            <TextField label="Observaciones" name="comentarios" value={form.comentarios} onChange={handleChange} multiline rows={4} fullWidth
                                helperText="Dejar constancia de excesos: certificaciones, recibos, etc." />

                            {/* Checklist en vivo (formularios + acta de nacimiento por hijo) */}
                            {(form.subtipo || hijosDelCliente.length > 0) && (
                                <Box sx={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', p: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600} color="#1e40af" sx={{ mb: 1 }}>
                                        <i className="bi bi-check2-square me-2"></i>
                                        Documentación requerida {cargandoChecklist && <CircularProgress size={14} sx={{ ml: 1 }} />}
                                    </Typography>
                                    <Divider sx={{ mb: 1 }} />
                                    {checklist.length === 0 && hijosDelCliente.length === 0 && !cargandoChecklist ? (
                                        <Typography variant="body2" color="text.secondary">Sin formularios para la selección actual.</Typography>
                                    ) : (
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {checklist.map(f => <Chip key={f.codigo} label={f.nombre} size="small" color="primary" variant="outlined" />)}
                                            {hijosDelCliente.map((h, idx) => (
                                                <Chip key={`acta-${idx}`} size="small" color="primary" variant="outlined"
                                                    label={`Acta de nacimiento — ${h.nombre || 'hijo sin nombre'}`} />
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                            )}

                            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>}
                            {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                            {tieneDuplicadoJubilacion && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Este cliente ya tiene {cantidadDuplicados === 1 ? 'un trámite de Jubilación' : `${cantidadDuplicados} trámites de Jubilación`}. ¿Está seguro de que desea crear otro?
                                </Alert>
                            )}
                            {tieneDuplicadoJubilacion && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={confirmarDuplicado}
                                            onChange={(e) => setConfirmarDuplicado(e.target.checked)}
                                        />
                                    }
                                    label="Confirmo que deseo continuar con un nuevo trámite de Jubilación igualmente."
                                />
                            )}
                            {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites de Jubilación activos del cliente...</Alert>}

                            <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
                                <Button fullWidth variant="outlined" size="large"
                                    onClick={() => onCancel ? onCancel() : navigate('/tramites')}
                                    sx={{ color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', textTransform: 'none' }}>
                                    <i className="bi bi-x-circle me-2"></i>Cancelar
                                </Button>
                                <Button fullWidth type="submit" size="large" disabled={!form.idCliente || !form.subtipo || guardando}
                                    sx={{
                                        background: 'var(--primary)', color: 'white', borderRadius: '10px',
                                        textTransform: 'none', fontWeight: 600,
                                        '&:hover': { background: 'var(--primary)' },
                                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                                    }}>
                                    {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <><i className="bi bi-check-circle me-2"></i>Iniciar Trámite</>}
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
                </Box>
            </Box>
        </div>
    );
}
